import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Crown,
  Eye,
  Loader2,
  Plus,
  Settings,
  Trash2,
  User,
  X,
  Zap,
} from "lucide-react";

import { API_BASE, TOASTPROPS } from "../shared/Constants";
import {
  OrganizationMember,
  OrganizationPermissionGroup,
  OrganizationSummary,
  OrganizationListPayload,
  OrganizationRolePermissionsResponse,
} from "../shared/Interfaces";
import {
  ORGANIZATION_ROLES,
  OrganizationPermission,
  OrganizationPermissionValue,
  OrganizationRoleValue,
  ROLE_LABELS,
  hasPermission,
} from "../shared/organizationPermissions";
import { reportHandledError, reportApiError } from "../utils/reportError";

import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "../lib/utils";

const getRoleDisplay = (role: string): { label: string; Icon: typeof Crown } => {
  const roleLower = (role || "").toLowerCase();
  switch (roleLower) {
    case "owner":
      return { label: "Proprietário", Icon: Crown };
    case "admin":
      return { label: "Admin", Icon: Zap };
    default:
      return { label: "Membro", Icon: User };
  }
};

const ROLE_ORDER: OrganizationRoleValue[] = [...ORGANIZATION_ROLES];

const cloneRolePermissionValues = (
  source: Record<OrganizationRoleValue, string[]>,
): Record<OrganizationRoleValue, string[]> => {
  return ROLE_ORDER.reduce((acc, role) => {
    acc[role] = [...(source[role] ?? [])];
    return acc;
  }, {} as Record<OrganizationRoleValue, string[]>);
};

const ensureUniqueSorted = (values: string[]): string[] => {
  return Array.from(new Set(values)).sort();
};

const deriveRolePermissionsFromOrganization = (
  org: OrganizationSummary,
): Record<OrganizationRoleValue, string[]> => {
  return ROLE_ORDER.reduce((acc, role) => {
    const stored = org.role_permissions?.[role] ?? [];
    acc[role] = stored.length > 0 ? ensureUniqueSorted(stored) : [];
    return acc;
  }, {} as Record<OrganizationRoleValue, string[]>);
};

function Organizations() {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [organizationName, setOrganizationName] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [orgToDelete, setOrgToDelete] = useState<OrganizationSummary | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [inviteEmails, setInviteEmails] = useState<Record<string, string>>({});
  const [invitingOrgId, setInvitingOrgId] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteEmailErrors, setInviteEmailErrors] = useState<Record<string, boolean>>({});
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [roleChangeSuccess, setRoleChangeSuccess] = useState<string | null>(null);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<string | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<{
    orgId: string;
    email: string;
    orgName: string;
  } | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{
    orgId: string;
    userId: string;
    email: string;
    orgName: string;
  } | null>(null);
  const [permissionGroups, setPermissionGroups] = useState<OrganizationPermissionGroup[]>([]);
  const [rolePermissionsModal, setRolePermissionsModal] = useState<{
    orgId: string;
    values: Record<OrganizationRoleValue, string[]>;
    initialValues: Record<OrganizationRoleValue, string[]>;
  } | null>(null);
  const [rolePermissionsSaving, setRolePermissionsSaving] = useState<boolean>(false);
  const [ownPermissionsModalInfo, setOwnPermissionsModalInfo] = useState<{
    orgId: string;
    permissions: string[];
    roleLabel: string;
    orgName: string;
  } | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoadingOrgs(true);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.list", method: "GET" });
        toast.error("Erro ao buscar organizações.", TOASTPROPS);
        return;
      }
      const data: OrganizationListPayload = await response.json();
      setOrganizations(data.organizations ?? []);
      setPermissionGroups(data.available_permissions ?? []);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.list" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    } finally {
      setLoadingOrgs(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreating(true);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/api/organizations/create`, {
        method: "POST",
        headers: {
          Authorization: `Auth ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: organizationName.trim() || null }),
      });
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.create", method: "POST" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          toast.error(parsed.detail || "Erro ao criar organização.", TOASTPROPS);
        } catch {
          toast.error("Erro ao criar organização.", TOASTPROPS);
        }
        return;
      }
      const data = await response.json();
      toast.success(data.message || "Organização criada com sucesso.", TOASTPROPS);
      setOrganizationName("");
      setIsModalOpen(false);
      fetchOrganizations();
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.create" });
      toast.error("Erro ao criar organização.", TOASTPROPS);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenModal = () => {
    setOrganizationName("");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setOrganizationName("");
    setIsModalOpen(false);
  };

  const toggleOrganization = (orgId: string) => {
    setExpandedOrgs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) newSet.delete(orgId);
      else newSet.add(orgId);
      return newSet;
    });
  };

  const handleDeleteOrganization = async (org: OrganizationSummary) => {
    setDeleting(true);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(
        `${API_BASE}/api/organizations/${org.organization_id}/delete`,
        {
          method: "DELETE",
          headers: { Authorization: `Auth ${accessToken}` },
        },
      );
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.delete", method: "DELETE" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          toast.error(parsed.detail || "Erro ao deletar organização.", TOASTPROPS);
        } catch {
          toast.error("Erro ao deletar organização.", TOASTPROPS);
        }
        return;
      }
      const data = await response.json();
      toast.success(data.message || "Organização deletada com sucesso.", TOASTPROPS);
      setOrgToDelete(null);
      fetchOrganizations();
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.delete" });
      toast.error("Erro ao deletar organização.", TOASTPROPS);
    } finally {
      setDeleting(false);
    }
  };

  const filteredOrganizations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return organizations;
    return organizations.filter(
      (org) =>
        (org.name || "").toLowerCase().includes(term) ||
        org.organization_id.toLowerCase().includes(term),
    );
  }, [search, organizations]);

  const handleInvitationInputChange = (orgId: string, value: string) => {
    setInviteEmails((prev) => ({ ...prev, [orgId]: value }));
    if (value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = emailRegex.test(value.trim());
      setInviteEmailErrors((prev) => ({ ...prev, [orgId]: !isValid }));
    } else {
      setInviteEmailErrors((prev) => ({ ...prev, [orgId]: false }));
    }
  };

  const handleInviteUser = async (orgId: string) => {
    const email = (inviteEmails[orgId] ?? "").trim();
    if (!email) {
      toast.error("Informe um e-mail válido.", TOASTPROPS);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Por favor, informe um e-mail válido.", TOASTPROPS);
      return;
    }
    setInvitingOrgId(orgId);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/api/organizations/${orgId}/invite`, {
        method: "POST",
        headers: {
          Authorization: `Auth ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.invite", method: "POST" });
        const errorPayload = await response.text();
        let errorMessage = "Erro ao enviar convite.";
        try {
          const parsed = JSON.parse(errorPayload);
          if (response.status === 422) {
            if (parsed.detail && Array.isArray(parsed.detail)) {
              const emailError = parsed.detail.find(
                (err: any) => err.loc && err.loc.includes("email"),
              );
              if (emailError) errorMessage = "Por favor, informe um e-mail válido.";
              else errorMessage = parsed.detail[0]?.msg || "Dados inválidos.";
            } else if (typeof parsed.detail === "string") {
              errorMessage = parsed.detail;
            } else {
              errorMessage = "Por favor, verifique os dados informados.";
            }
          } else {
            errorMessage = parsed.detail || errorMessage;
          }
        } catch {
          /* keep default */
        }
        toast.error(errorMessage, TOASTPROPS);
        return;
      }
      await response.json();
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) => {
          if (org.organization_id === orgId) {
            const newInvitation = {
              email,
              role: "member",
              invited_at: new Date().toISOString(),
              expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
              invited_by_user_id: user?.sub || "",
              invited_by_email: user?.email || "",
            };
            return {
              ...org,
              pending_invitations: [...(org.pending_invitations || []), newInvitation],
            };
          }
          return org;
        }),
      );
      setInviteEmails((prev) => ({ ...prev, [orgId]: "" }));
      setInviteEmailErrors((prev) => ({ ...prev, [orgId]: false }));
      setInviteSuccess(orgId);
      setTimeout(() => setInviteSuccess(null), 2000);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.invite" });
      toast.error("Erro ao enviar convite.", TOASTPROPS);
    } finally {
      setInvitingOrgId(null);
    }
  };

  const handleRoleChange = async (orgId: string, memberUserId: string, newRole: string) => {
    const changeKey = `${orgId}:${memberUserId}`;
    setChangingRole(changeKey);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(
        `${API_BASE}/api/organizations/${orgId}/members/${memberUserId}/role`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Auth ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ role: newRole }),
        },
      );
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.updateRole", method: "PATCH" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          toast.error(parsed.detail || "Erro ao alterar role.", TOASTPROPS);
        } catch {
          toast.error("Erro ao alterar role.", TOASTPROPS);
        }
        return;
      }
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) => {
          if (org.organization_id === orgId) {
            return {
              ...org,
              members: org.members?.map((member) =>
                member.user_id === memberUserId ? { ...member, role: newRole } : member,
              ),
            };
          }
          return org;
        }),
      );
      setRoleChangeSuccess(changeKey);
      setTimeout(() => setRoleChangeSuccess(null), 2000);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.updateRole" });
      toast.error("Erro ao alterar role.", TOASTPROPS);
    } finally {
      setChangingRole(null);
    }
  };

  const handleRemoveMember = async (
    orgId: string,
    memberUserId: string,
    _memberEmail: string,
  ) => {
    const changeKey = `${orgId}:${memberUserId}`;
    setRemovingMember(changeKey);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(
        `${API_BASE}/api/organizations/${orgId}/members/${memberUserId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Auth ${accessToken}` },
        },
      );
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.removeMember", method: "DELETE" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          toast.error(parsed.detail || "Erro ao remover membro.", TOASTPROPS);
        } catch {
          toast.error("Erro ao remover membro.", TOASTPROPS);
        }
        return;
      }
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) => {
          if (org.organization_id === orgId) {
            return {
              ...org,
              members: org.members?.filter((member) => member.user_id !== memberUserId),
            };
          }
          return org;
        }),
      );
      toast.success("Membro removido com sucesso.", TOASTPROPS);
      setMemberToRemove(null);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.removeMember" });
      toast.error("Erro ao remover membro.", TOASTPROPS);
    } finally {
      setRemovingMember(null);
    }
  };

  const computeInitialRolePermissions = (
    org: OrganizationSummary,
  ): Record<OrganizationRoleValue, string[]> => {
    return deriveRolePermissionsFromOrganization(org);
  };

  const openRolePermissionsModal = (org: OrganizationSummary) => {
    const initialValues = computeInitialRolePermissions(org);
    setRolePermissionsModal({
      orgId: org.organization_id,
      values: cloneRolePermissionValues(initialValues),
      initialValues: cloneRolePermissionValues(initialValues),
    });
    setRolePermissionsSaving(false);
  };

  const closeRolePermissionsModal = () => {
    if (rolePermissionsSaving) return;
    setRolePermissionsModal(null);
  };

  const toggleRolePermission = (
    role: OrganizationRoleValue,
    permission: OrganizationPermissionValue,
  ) => {
    if (!rolePermissionsModal || role === "owner") return;
    setRolePermissionsModal((prev) => {
      if (!prev) return prev;
      const current = prev.values[role] ?? [];
      const exists = current.includes(permission);
      const updated = exists
        ? current.filter((value) => value !== permission)
        : ensureUniqueSorted([...current, permission]);
      return {
        ...prev,
        values: { ...prev.values, [role]: updated },
      };
    });
  };

  const samePermissions = (a: string[] = [], b: string[] = []) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  const handleSaveRolePermissions = async () => {
    if (!rolePermissionsModal) return;
    const targetOrg = organizations.find(
      (org) => org.organization_id === rolePermissionsModal.orgId,
    );
    if (!targetOrg) {
      toast.error("Organização não encontrada.", TOASTPROPS);
      return;
    }
    const { values, initialValues } = rolePermissionsModal;
    const hasChanges = ROLE_ORDER.some(
      (role) =>
        !samePermissions(
          ensureUniqueSorted(values[role] ?? []),
          ensureUniqueSorted(initialValues[role] ?? []),
        ),
    );
    if (!hasChanges) {
      toast.info("Nenhuma alteração necessária.", TOASTPROPS);
      return;
    }
    setRolePermissionsSaving(true);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const payload = ROLE_ORDER.reduce((acc, role) => {
        acc[role] = ensureUniqueSorted(values[role] ?? []);
        return acc;
      }, {} as Record<OrganizationRoleValue, string[]>);
      const response = await fetch(
        `${API_BASE}/api/organizations/${rolePermissionsModal.orgId}/role-permissions`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Auth ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ role_permissions: payload }),
        },
      );
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.updatePermissions", method: "PATCH" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          throw new Error(parsed.detail || "Erro ao atualizar permissões.");
        } catch {
          throw new Error("Erro ao atualizar permissões.");
        }
      }
      const data: OrganizationRolePermissionsResponse = await response.json();
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) => {
          if (org.organization_id !== targetOrg.organization_id) return org;
          return {
            ...org,
            role_permissions: data.role_permissions,
            members: data.members ?? org.members,
          };
        }),
      );
      toast.success("Permissões por cargo atualizadas com sucesso.", TOASTPROPS);
      setRolePermissionsModal(null);
    } catch (error: any) {
      reportHandledError(error, { category: "api", feature: "organizations.updatePermissions" });
      toast.error(error?.message || "Erro ao atualizar permissões.", TOASTPROPS);
    } finally {
      setRolePermissionsSaving(false);
    }
  };

  const handleRevokeInvite = async (orgId: string, inviteEmail: string) => {
    const revokeKey = `${orgId}:${inviteEmail}`;
    setRevokingInvite(revokeKey);
    try {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const encodedEmail = encodeURIComponent(inviteEmail);
      const response = await fetch(
        `${API_BASE}/api/organizations/${orgId}/invitations/${encodedEmail}`,
        {
          method: "DELETE",
          headers: { Authorization: `Auth ${accessToken}` },
        },
      );
      if (!response.ok) {
        reportApiError(response, { feature: "organizations.revokeInvite", method: "DELETE" });
        const errorPayload = await response.text();
        try {
          const parsed = JSON.parse(errorPayload);
          toast.error(parsed.detail || "Erro ao revogar convite.", TOASTPROPS);
        } catch {
          toast.error("Erro ao revogar convite.", TOASTPROPS);
        }
        return;
      }
      setOrganizations((prevOrgs) =>
        prevOrgs.map((org) => {
          if (org.organization_id === orgId) {
            return {
              ...org,
              pending_invitations: org.pending_invitations?.filter(
                (invite) => invite.email.toLowerCase() !== inviteEmail.toLowerCase(),
              ),
            };
          }
          return org;
        }),
      );
      setInviteToRevoke(null);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "organizations.revokeInvite" });
      toast.error("Erro ao revogar convite.", TOASTPROPS);
    } finally {
      setRevokingInvite(null);
    }
  };

  const formatLocalizedDateTime = (value: string | null | undefined) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getMembershipInfo = useCallback(
    (org: OrganizationSummary) => {
      const member = org.members?.find((m) => m.user_id === currentUserId);
      const rawRole = (member?.role || "member").toLowerCase();
      const normalizedRole: OrganizationRoleValue = ROLE_ORDER.includes(
        rawRole as OrganizationRoleValue,
      )
        ? (rawRole as OrganizationRoleValue)
        : "member";
      const rolePermissionsMap = deriveRolePermissionsFromOrganization(org);
      const permissions = rolePermissionsMap[normalizedRole] ?? [];
      return { member, role: normalizedRole, permissions };
    },
    [currentUserId],
  );

  const canDeleteOrganization = useCallback(
    (org: OrganizationSummary): boolean => {
      const { role } = getMembershipInfo(org);
      return role === "owner";
    },
    [getMembershipInfo],
  );

  const canInviteMembers = useCallback(
    (org: OrganizationSummary): boolean => {
      const { permissions } = getMembershipInfo(org);
      return hasPermission(permissions, OrganizationPermission.ORG_INVITE_MEMBERS);
    },
    [getMembershipInfo],
  );

  const canChangeMemberRole = useCallback(
    (org: OrganizationSummary, targetMember: OrganizationMember): boolean => {
      if (!targetMember?.user_id) return false;
      if (targetMember.user_id === currentUserId) return false;
      const { role, permissions } = getMembershipInfo(org);
      if (!hasPermission(permissions, OrganizationPermission.ORG_MANAGE_ROLES)) return false;
      if (targetMember.role?.toLowerCase() === "owner" && role !== "owner") return false;
      return true;
    },
    [currentUserId, getMembershipInfo],
  );

  const canEditRolePermissions = useCallback(
    (org: OrganizationSummary): boolean => {
      const { permissions } = getMembershipInfo(org);
      return hasPermission(permissions, OrganizationPermission.ORG_MANAGE_PERMISSIONS);
    },
    [getMembershipInfo],
  );

  const canRemoveMember = useCallback(
    (org: OrganizationSummary, targetMember: OrganizationMember): boolean => {
      if (!targetMember?.user_id) return false;
      if (targetMember.user_id === currentUserId) return false;
      const { role, permissions } = getMembershipInfo(org);
      if (!hasPermission(permissions, OrganizationPermission.ORG_REMOVE_MEMBERS)) return false;
      if (targetMember.role?.toLowerCase() === "owner" && role !== "owner") return false;
      return true;
    },
    [currentUserId, getMembershipInfo],
  );

  return (
    <TooltipProvider>
      <div className="space-y-5">
        <p className="text-[13px] text-muted-foreground">
          Crie organizações para separar ambientes, equipes ou clientes e gere chaves de API
          exclusivas para cada uma delas.
        </p>

        {/* Header card with search + create */}
        <Card>
          <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
            <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
              Minhas organizações
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                placeholder="Buscar por nome ou ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="sm:w-64"
              />
              <Button onClick={handleOpenModal} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Criar Nova Organização
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Org list */}
        {loadingOrgs ? (
          <Card>
            <CardContent className="flex items-center justify-center gap-3 p-12 text-[13px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando organizações...
            </CardContent>
          </Card>
        ) : filteredOrganizations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 p-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-md border border-border bg-muted">
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-[14px] font-semibold text-foreground">
                Nenhuma organização encontrada
              </p>
              <p className="text-[13px] text-muted-foreground">
                Crie uma nova organização ou ajuste sua busca.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredOrganizations.map((org) => {
              const isExpanded = expandedOrgs.has(org.organization_id);
              const pendingInvites = org.pending_invitations ?? [];
              const hasPendingInvites = pendingInvites.length > 0;
              const userCanDelete = canDeleteOrganization(org);
              const userCanInvite = canInviteMembers(org);
              const membershipInfo = getMembershipInfo(org);
              const rolePermissionsMap = computeInitialRolePermissions(org);
              const membershipRole =
                (membershipInfo.role as OrganizationRoleValue) ?? "member";
              const ownRolePermissions =
                rolePermissionsMap[membershipRole] ?? rolePermissionsMap.member ?? [];
              const sortedOwnPermissions = ensureUniqueSorted(ownRolePermissions);
              const ownRoleLabel = ROLE_LABELS[membershipRole] ?? membershipRole;
              const sortedMembers = [...(org.members ?? [])];
              if (currentUserId) {
                const selfIndex = sortedMembers.findIndex(
                  (member) => member.user_id === currentUserId,
                );
                if (selfIndex > 0) {
                  const [selfMember] = sortedMembers.splice(selfIndex, 1);
                  sortedMembers.unshift(selfMember);
                }
              }
              const hasMembers = sortedMembers.length > 0;

              return (
                <Card key={org.organization_id} className="overflow-hidden">
                  {/* Org header */}
                  <div className="flex items-center justify-between gap-2 p-4">
                    <button
                      onClick={() => toggleOrganization(org.organization_id)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                        <Building2 className="h-4 w-4 text-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-semibold text-foreground">
                          {org.name ?? "(sem nome)"}
                        </p>
                        <p className="truncate font-mono text-[11px] text-muted-foreground">
                          {org.organization_id}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      {canEditRolePermissions(org) && (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRolePermissionsModal(org);
                              }}
                              aria-label="Configurar permissões"
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Configurar permissões por cargo</TooltipContent>
                        </Tooltip>
                      )}
                      {membershipInfo.member && (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOwnPermissionsModalInfo({
                                  orgId: org.organization_id,
                                  permissions: sortedOwnPermissions,
                                  roleLabel: ownRoleLabel,
                                  orgName: org.name || org.organization_id,
                                });
                              }}
                              aria-label="Ver minhas permissões"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver minhas permissões</TooltipContent>
                        </Tooltip>
                      )}
                      {userCanDelete && (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOrgToDelete(org);
                              }}
                              aria-label="Deletar organização"
                              className="hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Deletar organização</TooltipContent>
                        </Tooltip>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleOrganization(org.organization_id)}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted/20">
                      {userCanInvite && (
                        <>
                          <div className="space-y-2 p-4">
                            <p className="text-[13px] font-semibold text-foreground">
                              Convidar novo membro
                            </p>
                            <p className="text-[12px] text-muted-foreground">
                              Envie um convite por e-mail para adicionar alguém a esta
                              organização.
                            </p>
                            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-start">
                              <div className="flex-1">
                                <Input
                                  type="email"
                                  placeholder="pessoa@empresa.com"
                                  value={inviteEmails[org.organization_id] ?? ""}
                                  onChange={(event) =>
                                    handleInvitationInputChange(
                                      org.organization_id,
                                      event.target.value,
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      if (
                                        !inviteEmailErrors[org.organization_id] &&
                                        inviteEmails[org.organization_id]?.trim()
                                      ) {
                                        handleInviteUser(org.organization_id);
                                      }
                                    }
                                  }}
                                  className={cn(
                                    inviteEmailErrors[org.organization_id] &&
                                      "border-destructive focus-visible:ring-destructive",
                                  )}
                                />
                                {inviteEmailErrors[org.organization_id] && (
                                  <p className="mt-1 text-[11px] text-destructive">
                                    E-mail inválido
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  onClick={() => handleInviteUser(org.organization_id)}
                                  disabled={
                                    invitingOrgId === org.organization_id ||
                                    !!inviteEmailErrors[org.organization_id]
                                  }
                                  className="gap-1.5"
                                >
                                  {invitingOrgId === org.organization_id ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      Enviando...
                                    </>
                                  ) : (
                                    "Enviar convite"
                                  )}
                                </Button>
                                {inviteSuccess === org.organization_id && (
                                  <CheckCircle2 className="h-5 w-5 text-success" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-border" />
                        </>
                      )}

                      {/* Members list */}
                      <div className="p-4">
                        <p className="mb-2 text-[13px] font-semibold text-foreground">
                          Membros da organização
                        </p>
                        {hasMembers ? (
                          <div className="space-y-1">
                            {sortedMembers.map((member, index) => {
                              const memberKey = `${org.organization_id}:${member.user_id}`;
                              const canChangeRoleForMember = canChangeMemberRole(org, member);
                              const canRemoveMemberEntry = canRemoveMember(org, member);
                              const isChanging = changingRole === memberKey;
                              const showRoleSuccess = roleChangeSuccess === memberKey;
                              const isRemoving = removingMember === memberKey;
                              const roleInfo = getRoleDisplay(member.role || "member");
                              const RoleIconComp = roleInfo.Icon;
                              return (
                                <div
                                  key={index}
                                  className="flex items-center justify-between gap-3 rounded-md border border-transparent px-2 py-2 transition-colors hover:border-border hover:bg-background"
                                >
                                  <div className="flex min-w-0 items-center gap-2.5">
                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                                      <User className="h-3.5 w-3.5" />
                                    </div>
                                    <span className="truncate text-[13px] text-foreground">
                                      {member.email}
                                    </span>
                                  </div>
                                  <div className="flex shrink-0 items-center gap-2">
                                    {canChangeRoleForMember ? (
                                      <>
                                        <Select
                                          value={member.role}
                                          onValueChange={(v) =>
                                            handleRoleChange(
                                              org.organization_id,
                                              member.user_id,
                                              v,
                                            )
                                          }
                                          disabled={isChanging || isRemoving}
                                        >
                                          <SelectTrigger className="h-8 w-[160px] text-[12px]">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="owner">
                                              <span className="flex items-center gap-2">
                                                <Crown className="h-3.5 w-3.5" />
                                                Proprietário
                                              </span>
                                            </SelectItem>
                                            <SelectItem value="admin">
                                              <span className="flex items-center gap-2">
                                                <Zap className="h-3.5 w-3.5" />
                                                Admin
                                              </span>
                                            </SelectItem>
                                            <SelectItem value="member">
                                              <span className="flex items-center gap-2">
                                                <User className="h-3.5 w-3.5" />
                                                Membro
                                              </span>
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                        {isChanging && (
                                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                        )}
                                        {showRoleSuccess && (
                                          <CheckCircle2 className="h-4 w-4 text-success" />
                                        )}
                                      </>
                                    ) : (
                                      <Badge variant="secondary" className="gap-1">
                                        <RoleIconComp className="h-3 w-3" />
                                        {roleInfo.label}
                                      </Badge>
                                    )}
                                    {canRemoveMemberEntry && (
                                      <Tooltip delayDuration={300}>
                                        <TooltipTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={() =>
                                              setMemberToRemove({
                                                orgId: org.organization_id,
                                                userId: member.user_id,
                                                email: member.email,
                                                orgName: org.name || org.organization_id,
                                              })
                                            }
                                            disabled={isRemoving || isChanging}
                                            aria-label="Remover membro"
                                            className="hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                                          >
                                            {isRemoving ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                              <X className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Remover membro</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="rounded-md border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground">
                            Nenhum membro adicionado a esta organização ainda.
                          </p>
                        )}
                      </div>

                      <div className="border-t border-border" />

                      {/* Pending invitations */}
                      <div className="p-4">
                        <p className="mb-2 text-[13px] font-semibold text-foreground">
                          Convites pendentes
                        </p>
                        {hasPendingInvites ? (
                          <div className="space-y-1">
                            {pendingInvites.map((invite, index) => {
                              const expiresText = formatLocalizedDateTime(invite.expires_at);
                              const roleInfo = getRoleDisplay(invite.role);
                              const RoleIconComp = roleInfo.Icon;
                              const revokeKey = `${org.organization_id}:${invite.email}`;
                              const isRevoking = revokingInvite === revokeKey;
                              return (
                                <div
                                  key={`${invite.email}-${index}`}
                                  className="flex items-start justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                                >
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-[13px] font-medium text-foreground">
                                      {invite.email}
                                    </p>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                      <span className="inline-flex items-center gap-1">
                                        <RoleIconComp className="h-3 w-3" />
                                        {roleInfo.label}
                                      </span>
                                      <span>·</span>
                                      <span>Expira em {expiresText}</span>
                                      {invite.invited_by_email && (
                                        <>
                                          <span>·</span>
                                          <span>Enviado por {invite.invited_by_email}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {userCanInvite && (
                                    <Tooltip delayDuration={300}>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="icon"
                                          onClick={() =>
                                            setInviteToRevoke({
                                              orgId: org.organization_id,
                                              email: invite.email,
                                              orgName: org.name || org.organization_id,
                                            })
                                          }
                                          disabled={isRevoking}
                                          aria-label="Revogar convite"
                                          className="hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                                        >
                                          {isRevoking ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <X className="h-4 w-4" />
                                          )}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Revogar convite</TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="rounded-md border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground">
                            Nenhum convite pendente. Convide alguém para começar.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Create org modal */}
        <Dialog open={isModalOpen} onOpenChange={(o) => !o && handleCloseModal()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar nova organização</DialogTitle>
              <DialogDescription>
                Escolha um nome para identificar sua organização.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="org-name">Nome da organização</Label>
                <Input
                  id="org-name"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Equipe de Pesquisa"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseModal} disabled={creating}>Cancelar</Button>
                <Button type="submit" disabled={creating} className="gap-1.5">
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    "Criar organização"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Confirm delete org */}
        <Dialog open={orgToDelete !== null} onOpenChange={(o) => !o && setOrgToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Exclusão</DialogTitle>
              <DialogDescription>
                Esta ação não pode ser desfeita. Tem certeza que deseja deletar a organização{" "}
                <strong className="text-foreground">
                  {orgToDelete?.name || orgToDelete?.organization_id}
                </strong>
                ? Todas as chaves de API associadas também serão desativadas.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p className="text-[13px] text-foreground/80">
                Confirma a exclusão? Esta ação é permanente.
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setOrgToDelete(null)}
                disabled={deleting}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => orgToDelete && handleDeleteOrganization(orgToDelete)}
                disabled={deleting}
                className="flex-1 gap-1.5"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deletando...
                  </>
                ) : (
                  "Confirmar Exclusão"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm revoke invite */}
        <Dialog
          open={inviteToRevoke !== null}
          onOpenChange={(o) => !o && !revokingInvite && setInviteToRevoke(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revogar Convite</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja revogar o convite para{" "}
                <strong className="text-foreground">{inviteToRevoke?.email}</strong> na
                organização{" "}
                <strong className="text-foreground">{inviteToRevoke?.orgName}</strong>? O link
                de convite não será mais válido.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setInviteToRevoke(null)}
                disabled={revokingInvite !== null}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  inviteToRevoke &&
                  handleRevokeInvite(inviteToRevoke.orgId, inviteToRevoke.email)
                }
                disabled={revokingInvite !== null}
                className="flex-1 gap-1.5"
              >
                {revokingInvite !== null ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Revogando...
                  </>
                ) : (
                  "Revogar Convite"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Configure role permissions */}
        <Dialog
          open={rolePermissionsModal !== null}
          onOpenChange={(o) => !o && closeRolePermissionsModal()}
        >
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configurar permissões por cargo</DialogTitle>
              <DialogDescription>
                Defina quais permissões cada cargo possui. As alterações se aplicam a todos os
                membros do cargo.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-[13px] text-foreground/80">
              💡 <strong className="text-foreground">Proprietários</strong> sempre têm todas as
              permissões ativadas automaticamente.
            </div>
            {rolePermissionsModal && permissionGroups.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
                Nenhuma permissão disponível para edição no momento.
              </p>
            ) : (
              <div className="space-y-4">
                {ROLE_ORDER.filter((role) => role !== "owner").map((role) => (
                  <Card key={role}>
                    <div className="flex items-center gap-2 border-b border-border p-3">
                      {role === "admin" ? (
                        <Zap className="h-4 w-4 text-foreground" />
                      ) : (
                        <User className="h-4 w-4 text-foreground" />
                      )}
                      <h4 className="text-[14px] font-semibold text-foreground">
                        {ROLE_LABELS[role]}
                      </h4>
                    </div>
                    <div className="space-y-3 p-3">
                      {permissionGroups.map((group) => (
                        <div key={group.category}>
                          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {group.category_label}
                          </p>
                          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                            {group.permissions.map((permission) => {
                              const valuesForRole = rolePermissionsModal?.values[role] ?? [];
                              const isChecked = valuesForRole.includes(permission.value);
                              return (
                                <label
                                  key={permission.value}
                                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() =>
                                      toggleRolePermission(
                                        role,
                                        permission.value as OrganizationPermissionValue,
                                      )
                                    }
                                    className="h-4 w-4 rounded border-border accent-primary"
                                  />
                                  <span className="text-[13px] text-foreground">
                                    {permission.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={closeRolePermissionsModal}
                disabled={rolePermissionsSaving}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSaveRolePermissions}
                disabled={rolePermissionsSaving}
                className="flex-1 gap-1.5"
              >
                {rolePermissionsSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar alterações"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View own permissions */}
        <Dialog
          open={ownPermissionsModalInfo !== null}
          onOpenChange={(o) => !o && setOwnPermissionsModalInfo(null)}
        >
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Suas permissões</DialogTitle>
              <DialogDescription>
                {ownPermissionsModalInfo ? (
                  <>
                    Permissões atribuídas ao cargo{" "}
                    <strong className="text-foreground">
                      {ownPermissionsModalInfo.roleLabel}
                    </strong>{" "}
                    na organização{" "}
                    <strong className="text-foreground">
                      {ownPermissionsModalInfo.orgName}
                    </strong>
                    .
                  </>
                ) : (
                  "Permissões do seu cargo."
                )}
              </DialogDescription>
            </DialogHeader>
            {permissionGroups.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
                Nenhuma permissão disponível para visualização no momento.
              </p>
            ) : (
              <div className="space-y-3">
                {permissionGroups.map((group) => (
                  <Card key={group.category}>
                    <div className="border-b border-border p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.category_label}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-2">
                      {group.permissions.map((permission) => {
                        const isChecked =
                          ownPermissionsModalInfo?.permissions.includes(permission.value) ??
                          false;
                        return (
                          <label
                            key={permission.value}
                            className="flex items-center gap-2 px-2 py-1.5 opacity-90"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled
                              className="h-4 w-4 rounded border-border accent-primary"
                            />
                            <span className="text-[13px] text-foreground">{permission.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </Card>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOwnPermissionsModalInfo(null)}
                className="w-full"
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm remove member */}
        <Dialog
          open={memberToRemove !== null}
          onOpenChange={(o) => !o && !removingMember && setMemberToRemove(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remover Membro</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja remover{" "}
                <strong className="text-foreground">{memberToRemove?.email}</strong> da
                organização{" "}
                <strong className="text-foreground">{memberToRemove?.orgName}</strong>? O
                membro perderá acesso imediatamente.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setMemberToRemove(null)}
                disabled={removingMember !== null}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  memberToRemove &&
                  handleRemoveMember(
                    memberToRemove.orgId,
                    memberToRemove.userId,
                    memberToRemove.email,
                  )
                }
                disabled={removingMember !== null}
                className="flex-1 gap-1.5"
              >
                {removingMember !== null ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Removendo...
                  </>
                ) : (
                  "Remover Membro"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </TooltipProvider>
  );
}

export default Organizations;
